import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import { useUnsavedGuard } from "./unsavedGuard";
import { SavedNote } from "../shared/Icons";

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta"];
interface AvailableModel { id: string; provider: string; model_name: string; label: string }

// Modelos TTS do Gemini (Interactions API). Espelho de GEMINI_TTS_MODELS em voice.py.
const GEMINI_TTS_MODELS: [string, string][] = [
  ["gemini-3.8-flash-lite-tts", "Gemini 3.8 Flash Lite TTS (rápido, US$ 6/M)"],
  ["gemini-3.8-flash-tts", "Gemini 3.8 Flash TTS (fidelidade, US$ 9/M)"],
];
// Vozes pré-definidas (ouça em aistudio.google.com).
const GEMINI_VOICES = [
  "Aoede", "Charon", "Fenrir", "Kore", "Leda", "Orus", "Puck", "Zephyr",
  "Achernar", "Achird", "Algenib", "Algieba", "Alnilam", "Autonoe", "Callirrhoe", "Despina",
  "Enceladus", "Erinome", "Gacrux", "Iapetus", "Laomedeia", "Pulcherrima", "Rasalgethi",
  "Sadachbia", "Sadaltager", "Schedar", "Sulafat", "Umbriel", "Vindemiatrix", "Zubenelgenubi",
];

// Espelho de PRE_TTS_HEADER em carmillion_core/voice.py — manter iguais.
const PRE_TTS_HEADER =
  "Você está fazendo um ajuste textual da mensagem recebida para gerar um áudio em outro sistema. " +
  "Siga as regras abaixo para ajustar a escrita. Seu output deve ser somente a frase ajustada e nada mais.";

export function ConfigVoz({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const v = agent.voice ?? {};

  const [enabled, setEnabled] = useState<boolean>(v.enabled ?? false);
  // "cartesia" | "gemini" (Gemini TTS via Interactions API — alpha)
  const [provider, setProvider] = useState<string>(v.provider ?? "cartesia");
  const [geminiModel, setGeminiModel] = useState<string>(
    GEMINI_TTS_MODELS.some(([id]) => id === v.gemini_model) ? v.gemini_model : "gemini-3.8-flash-lite-tts",
  );
  const [geminiVoice, setGeminiVoice] = useState<string>(v.gemini_voice || "Kore");
  const [geminiInstruction, setGeminiInstruction] = useState<string>(v.gemini_instruction ?? "");
  const [geminiSpeed, setGeminiSpeed] = useState<number>(v.gemini_speed ?? 1.0);
  const [voiceId, setVoiceId] = useState<string>(v.cartesia_voice_id ?? "");
  const [modelId, setModelId] = useState<string>(v.model_id || "sonic-3.6");
  const [locale, setLocale] = useState<string>(v.locale || "pt-BR");
  const [speed, setSpeed] = useState<number>(v.speed ?? 1.0);
  const [emotion, setEmotion] = useState<string>(v.emotion ?? "");
  // Pré-TTS: adapta o texto do agente para fala (era o subagente global "voz").
  const [ttsPrompt, setTtsPrompt] = useState<string>(v.tts_prompt ?? "");
  const [ttsProvider, setTtsProvider] = useState<string>(v.tts_provider ?? "openai");
  const [ttsModel, setTtsModel] = useState<string>(v.tts_model ?? "gpt-4o-mini");
  const [ttsTemperature, setTtsTemperature] = useState<number>(v.tts_temperature ?? 0.2);

  const { data: catalog = [] } = useQuery<AvailableModel[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const providerModels = catalog.filter((m) => m.provider === ttsProvider);
  const modelOptions: AvailableModel[] = !ttsModel || providerModels.some((m) => m.model_name === ttsModel)
    ? providerModels
    : [{ id: "__current__", provider: ttsProvider, model_name: ttsModel, label: `${ttsModel} (atual)` }, ...providerModels];
  function handleProviderChange(p: string) {
    setTtsProvider(p);
    setTtsModel(catalog.find((m) => m.provider === p)?.model_name ?? "");
  }

  const geminiCfg = {
    provider, gemini_model: geminiModel, gemini_voice: geminiVoice, gemini_instruction: geminiInstruction,
    gemini_speed: geminiSpeed,
  };
  const snap = () => JSON.stringify({ enabled, voiceId, modelId, locale, speed, emotion, ttsPrompt, ttsProvider, ttsModel, ttsTemperature, geminiCfg });
  const current = snap();
  const [savedSnap, setSavedSnap] = useState(current);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/agents/${agent.id}`, {
        voice: {
          enabled,
          ...geminiCfg,
          cartesia_voice_id: voiceId,
          model_id: modelId,
          locale,
          speed,
          emotion,
          tts_prompt: ttsPrompt,
          tts_provider: ttsProvider,
          tts_model: ttsModel,
          tts_temperature: ttsTemperature,
        },
      }),
    onSuccess: () => {
      setSavedSnap(snap());
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
  });

  useUnsavedGuard(current !== savedSnap, mut.mutateAsync);

  // Teste de voz: efêmero (nada salvo), usa os valores do form mesmo sem salvar.
  const [testText, setTestText] = useState("");
  const [testPreVoz, setTestPreVoz] = useState(false);
  const test = useMutation({
    mutationFn: () =>
      api.post<{ audio_b64: string | null; mime?: string; spoken_text?: string; error: string | null }>(
        `/agents/${agent.id}/voice/test`,
        {
          text: testText, ...geminiCfg, cartesia_voice_id: voiceId, model_id: modelId, locale, speed, emotion,
          tts_enabled: testPreVoz, tts_prompt: ttsPrompt, tts_provider: ttsProvider, tts_model: ttsModel, tts_temperature: ttsTemperature,
        },
        60_000,
      ),
  });

  return (
    <div style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="checkbox"
          id="voz-enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          style={{ accentColor: "var(--brand)", width: 16, height: 16 }}
        />
        <label htmlFor="voz-enabled" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
          Habilitar voz
        </label>
      </div>

      {enabled && (
        <>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Provedor de voz</label>
            <select style={inputStyle} value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="cartesia">Cartesia</option>
              <option value="gemini">Gemini TTS (Alpha)</option>
            </select>
          </div>

          {provider === "gemini" && (<>
            <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>
              Alpha: modelos de texto-para-fala do Gemini. Leem o texto do agente (após o Pré-TTS) ao pé da letra;
              o estilo vai em campo separado. Usa a chave API do provider <b>google</b>.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Modelo</label>
                <select style={inputStyle} value={geminiModel} onChange={(e) => setGeminiModel(e.target.value)}>
                  {GEMINI_TTS_MODELS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Voz</label>
                <select style={inputStyle} value={geminiVoice} onChange={(e) => setGeminiVoice(e.target.value)}>
                  {!GEMINI_VOICES.includes(geminiVoice) && <option value={geminiVoice}>{geminiVoice} (atual)</option>}
                  {GEMINI_VOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Velocidade (aplicada no envio; o teste abaixo toca na velocidade original)</label>
                <span style={{ fontSize: 12, color: "var(--ink)" }}>{geminiSpeed.toFixed(2)}x</span>
              </div>
              <input type="range" min={0.5} max={2.0} step={0.05} value={geminiSpeed}
                onChange={(e) => setGeminiSpeed(parseFloat(e.target.value))}
                style={{ width: "100%", accentColor: "var(--brand)" }} />
            </div>
            <div>
              <label style={labelStyle}>Estilo de fala (tom, ritmo, sotaque; ex.: "simpática, ritmo acelerado")</label>
              <textarea
                style={{ ...inputStyle, minHeight: 90, resize: "vertical", fontFamily: "inherit" }}
                value={geminiInstruction}
                onChange={(e) => setGeminiInstruction(e.target.value)}
                placeholder="simpática e profissional, como uma pessoa conversando no WhatsApp, ritmo levemente acelerado"
              />
            </div>
          </>)}

          {provider === "cartesia" && (<>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Cartesia Voice ID</label>
            <input
              style={inputStyle}
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder="ex: 694f9389-aac1-45b6-b726-9d9369183238"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Model ID</label>
            <input
              style={inputStyle}
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="sonic-3.6"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Locale</label>
            <input
              style={inputStyle}
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              placeholder="pt-BR"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Speed</label>
              <span style={{ fontSize: 12, color: "var(--ink)" }}>{speed.toFixed(2)}x</span>
            </div>
            <input
              type="range"
              min={0.5}
              max={2.0}
              step={0.05}
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: "var(--brand)" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ink-muted)", marginTop: 4 }}>
              <span>0.5x</span>
              <span>2.0x</span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Emoção</label>
            <input
              style={inputStyle}
              value={emotion}
              onChange={(e) => setEmotion(e.target.value)}
              placeholder="ex: positivity:high"
            />
          </div>
          </>)}

          <div style={{ borderTop: "1px solid var(--line)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Testar voz</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Texto (usa os parâmetros acima, mesmo sem salvar)</label>
              <span style={{ fontSize: 11, color: testText.length > 450 ? "var(--danger)" : "var(--ink-muted)" }}>{testText.length}/450</span>
            </div>
            <textarea
              style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: "inherit" }}
              value={testText}
              maxLength={450}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Olá! Aqui é a Helena da Carmillion Motors. Como posso te ajudar hoje?"
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="voz-test-prevoz"
                checked={testPreVoz}
                onChange={(e) => setTestPreVoz(e.target.checked)}
                style={{ accentColor: "var(--brand)", width: 16, height: 16 }}
              />
              <label htmlFor="voz-test-prevoz" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
                Processar o texto com o agente de Pré-Voz
              </label>
            </div>
            <button
              onClick={() => test.mutate()}
              disabled={test.isPending || !(provider === "gemini" ? geminiVoice.trim() : voiceId.trim()) || !testText.trim()}
              style={{
                background: "var(--surface-input)",
                color: "var(--ink)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "8px 20px",
                fontSize: 13,
                cursor: "pointer",
                alignSelf: "flex-start",
              }}
            >
              {test.isPending ? "Gerando..." : "Testar"}
            </button>
            {test.isError && <div style={{ fontSize: 12, color: "var(--danger)" }}>{String(test.error)}</div>}
            {test.data?.error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{test.data.error}</div>}
            {test.data?.audio_b64 && (
              <audio controls autoPlay src={`data:${test.data.mime ?? "audio/mpeg"};base64,${test.data.audio_b64}`} style={{ width: "100%" }} />
            )}
            {test.data?.spoken_text && test.data.spoken_text !== testText && (
              <div style={{ fontSize: 12, color: "var(--ink-muted)", whiteSpace: "pre-wrap" }}>
                <b>Texto falado (pós Pré-Voz):</b> {test.data.spoken_text}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid var(--line)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Pré-TTS (adaptação do texto para fala)</div>
            <div>
              <label style={labelStyle}>Cabeçalho fixo (injetado no início de toda chamada)</label>
              <div style={{ ...inputStyle, background: "var(--surface-hover)", color: "var(--ink-muted)", whiteSpace: "pre-wrap", cursor: "default" }}>
                {PRE_TTS_HEADER}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Regras (vazio = fala o texto do agente sem adaptar)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 110, resize: "vertical", fontFamily: "inherit" }}
                value={ttsPrompt}
                onChange={(e) => setTtsPrompt(e.target.value)}
                placeholder={"- Substituir Chevrolet por \"Chevrolé\"\n- KW - ler como Kilowatts\n- Letras e números juntos no nome do veículo: S10 se lê \"S dez\""}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {!agent.template_id && (<>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Provider</label>
                <select style={inputStyle} value={ttsProvider} onChange={(e) => handleProviderChange(e.target.value)}>
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Modelo</label>
                {modelOptions.length === 0 ? (
                  <div style={{ fontSize: 11, color: "var(--ink-soft)", padding: "8px 0" }}>Nenhum modelo cadastrado (Painel Dev → Modelos).</div>
                ) : (
                  <select style={inputStyle} value={ttsModel} onChange={(e) => setTtsModel(e.target.value)}>
                    {modelOptions.map((m) => <option key={m.model_name} value={m.model_name}>{m.label}</option>)}
                  </select>
                )}
              </div>
              </>)}
              <div style={{ width: 110 }}>
                <label style={labelStyle}>Temperatura</label>
                <input type="number" step={0.05} min={0} max={2} style={inputStyle}
                  value={ttsTemperature} onChange={(e) => setTtsTemperature(parseFloat(e.target.value))} />
              </div>
            </div>
          </div>

        </>
      )}

      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        style={{
          background: "var(--brand)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "8px 20px",
          fontSize: 13,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>
      <SavedNote show={mut.isSuccess} />
    </div>
  );
}
